<template>
  <div class="layout-fullscreen layout-fullscreen--no-header">
    <div class="header-nav-wrapper">
      <header class="header-nav">
        <!-- Back button -->
        <div class="header-nav__left">
          <router-link
            v-if="activeOperation"
            class="button"
            :to="{ path: activeOperation }"
          >
            <UseSvg
              class="icon"
              name="arrow_left"
            />
          </router-link>
        </div>
        <!-- Header title -->
        <div class="header-nav__center hide-for-medium">
          {{ $t('user_operations.users') }}
        </div>
        <div class="header-nav__left show-for-medium">
          {{ $t('user_operations.users') }}
        </div>
        <!-- Header actions -->
        <div class="header-nav__right show-for-medium">
          <div class="menu">
            <ul class="menu__items">
              <li class="menu-item typo-list-item">
                <div class="input">
                  <div class="input__prefix">
                    <UseSvg
                      class="icon"
                      name="search"
                    />
                  </div>
                  <input
                    v-model="search"
                    :aria-label="$t('user_operations.search_users')"
                    :placeholder="$t('user_operations.search_users')"
                  >
                  <button
                    v-if="searchResults"
                    class="input__clear"
                    @click="clearSearch"
                  >
                    <UseSvg
                      class="icon"
                      name="close_circle_24"
                    />
                  </button>
                </div>
              </li>
              <li class="menu-item typo-list-item">
                <router-link
                  class="button"
                  :to="{ name: 'inviteUser' }"
                >
                  {{ $t('user_operations.invite') }}
                </router-link>
              </li>
            </ul>
          </div>
        </div>
      </header>
    </div>

    <!-- Mobile search -->
    <div :class="{ 'mobile-overlay hide-for-medium': true, 'hide': !mobileSearchOpen }">
      <div class="form form--flat">
        <div class="form__row">
          <div class="input input--light input--flat input--search">
            <div class="input__prefix">
              <UseSvg
                class="icon"
                name="search"
              />
            </div>
            <input
              v-model="search"
              :aria-label="$t('user_operations.search_users')"
              :placeholder="$t('user_operations.search_users')"
            >
            <button
              class="input__clear"
              @click="clearSearch"
            >
              <UseSvg
                class="icon"
                name="close_circle_24"
              />
            </button>
          </div>
        </div>
      </div>

      <template v-if="search && searchResults">
        <HeaderBar :modifiers="['narrow', 'darkest', 'transparent-on-medium']">
          <span class="typo-small">{{ $t('general.results') }}</span>
        </HeaderBar>
        <div v-if="searchResults.length > 0">
          <UserList
            :roles="roles"
            :users="searchResults"
          />
        </div>
        <EmptyMessage
          v-else
          :message="$t('general.no_results')"
        >
          <UseSvg name="magnifying_glass_circle" />
        </EmptyMessage>
      </template>
    </div>

    <div class="layout-fullscreen__container">
      <!-- Mobile header actions -->
      <HeaderBar
        class="hide-for-medium"
        :modifiers="['light']"
      >
        <div
          class="header-bar__title typo-title"
          @click="mobileOperationsPanelOpen"
        >
          {{ currentOperationName }}
          <UseSvg
            class="icon icon--small"
            name="arrow_down_16"
          />
        </div>

        <template #right>
          <div class="menu">
            <ul class="menu__items">
              <li
                class="menu-item"
                @click="openMobileSearch"
              >
                <UseSvg
                  class="icon"
                  name="search"
                />
              </li>
              <li class="menu-item">
                <router-link :to="{ name: 'inviteUser' }">
                  <UseSvg
                    class="icon"
                    name="plus"
                  />
                </router-link>
              </li>
            </ul>
          </div>
        </template>
      </HeaderBar>

      <!-- operation nav items -->
      <div class="layout-panel">
        <div :class="{'layout-panel__panel layout-panel__panel--padded layout-panel__panel--foldable' : true, 'layout-panel__panel--shown' : mobilePanelShowing}">
          <div class="mobile-menu__header hide-for-medium">
            <div
              class="mobile-menu__close"
              @click="mobileOperationsPanelClose"
            >
              <UseSvg
                class="icon"
                name="close"
              />
            </div>
            <div class="mobile-menu__title typo-nav-item">
              <span>{{ $t('user_operations.select_operation') }}</span>
            </div>
          </div>
          <Loader v-if="loading" />
          <div
            v-if="!loading"
            class="menu menu--vertical menu--vertical-scroll"
          >
            <UserAccordion
              v-for="(value, name, index) in userData"
              :key="index"
              :active="fetchPanelIndex(name, index)"
            >
              <template #header-main>
                <div>
                  {{ $t(`nav.${name}`) }}
                </div>
              </template>
              <template #header-right>
                <div class="typo-tiny">
                  {{
                    pluralize(
                      value.operations.length,
                      $t('activerecord.models.operation.one'),
                      $t('activerecord.models.operation.other')
                    )
                  }}
                </div>
              </template>
              <template #content>
                <ul class="menu__items">
                  <li @click="setStorage(storageName, index, true)">
                    <router-link
                      activeClass="menu-item--active"
                      class="menu-item__link menu-item typo-list-item menu-item--vertical"
                      :to="{ name: 'allUsersScoped', params: { type: getType(name) } }"
                    >
                      <span class="menu-item__label">
                        {{ $t('user_operations.all_users') }}
                      </span>
                    </router-link>
                  </li>
                  <li
                    v-for="(operation) in value.operations"
                    :key="operation.id"
                    @click="updateRoleList(name); setStorage(storageName, index, true)"
                  >
                    <router-link
                      activeClass="menu-item--active"
                      class="menu-item__link menu-item typo-list-item menu-item--vertical"
                      :to="{name: 'operationUsers', params: { operationId: operation.id }}"
                    >
                      <span class="menu-item__label">
                        {{ operation.name }}
                      </span>
                    </router-link>
                  </li>
                </ul>
              </template>
            </UserAccordion>
          </div>
        </div>
        <!-- user list -->
        <div class="layout-panel__panel">
          <Loader v-if="loading" />
          <div
            v-if="!loading"
            class="scroll-container"
          >
            <template v-if="search && searchResults">
              <div v-if="searchResults.length > 0">
                <UserList
                  :roles="roles"
                  :users="searchResults"
                />
              </div>
              <EmptyMessage
                v-else
                :message="$t('general.no_results')"
              >
                <UseSvg name="magnifying_glass_circle" />
              </EmptyMessage>
            </template>
            <UserList
              v-else
              :roles="roles"
              :users="operationUsers"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

// Utils
import useAjax from '@/functions/use_ajax';
import I18n from 'i18n-js';

import Searcher from '@/functions/searcher';
import pluralize from '@/functions/pluralize';

// Components
import EmptyMessage from '@/components/generic/empty_message.vue';
import HeaderBar from '@/components/generic/header_bar.vue';
import Loader from '@/components/generic/loader.vue';
import UserList from '@/components/users/user_list.vue';
import UseSvg from '@/components/generic/use_svg.vue';
import UserAccordion from '@/components/users/user_accordion.vue';
import { setStorage, deleteStorage, fetchIndex } from '@/functions/local_storage.js';

const props = defineProps({
  organisationId: { type: String, required: true },
  operationId: { type: String, required: false, default: undefined },
});

const ajax = useAjax();
const route = useRoute();
const router = useRouter();

const allUsers = ref(null);
const loading = ref(true);
const mobilePanelShowing = ref(false);
const mobileSearchOpen = ref(false);
const operations = ref(null);
const operationUsers = ref();
const search = ref('');
const searchResults = ref(null);
const roles = ref();
const userData = ref(null);
const storageName = 'userManagementPanel';
const storageOperationName = 'userManagementOperation';
const activeOperation = ref(null);

let searcher;

/**
 * Display correct operation name in mobile select menu
 */
const currentOperationName = computed(() => {
  let currentOperation;

  if (operations.value && props.operationId) {
    currentOperation = operations.value.find((operation) => operation.id === props.operationId).name;
  } else {
    currentOperation = I18n.t('user_operations.all_users');
  }
  return currentOperation;
});

onMounted(() => {
  const backRoute = router?.options?.history?.state?.back;
  const isFromUserPage = backRoute?.includes('users');
  if (route.params.type && !isFromUserPage) {
    setStorage(storageName, mapNameToIndex(route.params.type), true);
    setStorage(storageOperationName, backRoute, true);
  }
  activeOperation.value = fetchIndex(storageOperationName) || 'operations';
  fetchOperationsAndUsers();
});

onUnmounted(() => {
  const condition = ['operationUsers', 'editUser', 'allUsers', 'allUsersScoped', 'inviteUser', 'userProfile', 'userPassword'];
  deleteStorage(storageName, route.name, condition);
  deleteStorage(storageOperationName, route.name, condition);
});

/**
 * Watch for operations id changes and get data
 */
watch(
  () => props.operationId,
  () => {
    getOperationUsers(props.operationId);
    mobileOperationsPanelClose();
    clearSearch();
  }
);

watch(
  () => route.params.type,
  () => {
    getOperationUsers(props.operationId);
    mobileOperationsPanelClose();
  }
);

/**
 * Watch and set search
 */
watch(search, () => {
  if (search.value) {
    searchResults.value = searcher.search(search.value);
  } else {
    searchResults.value = null;
  }
});

async function fetchOperationsAndUsers() {
  let { ok, data } = await ajax(`/organisations/${props.organisationId}/users`);

  if (ok) {
    userData.value = data;

    allUsers.value = [
      ...data.plants.users,
      ...data.shafts.users,
      ...data.shafts_v2_shafts.users,
      ...data.ships.users,
      ...data.equipment.users,
    ];
    operations.value = [
      ...data.plants.operations,
      ...data.shafts.operations,
      ...data.shafts_v2_shafts.operations,
      ...data.ships.operations,
      ...data.equipment.operations,
    ];
    roles.value = data.plants.roles;

    getOperationUsers(props.operationId);

    loading.value = false;
    searcher = new Searcher(allUsers.value, {
      keys: ['first_name', 'last_name', 'email'],
    });
  }
}

/**
 * Update the role list on user selection
 * on the side nav
 */
function updateRoleList(type) {
  switch (type) {
    case 'shafts':
      roles.value = userData.value.shafts.roles;
      break;
    case 'shafts_v2_shafts':
      roles.value = userData.value.shafts_v2_shafts.roles;
      break;
    case 'ships':
      roles.value = userData.value.ships.roles;
      break;
    case 'equipment':
      roles.value = userData.value.equipment.roles;
      break;
    default:
      roles.value = userData.value.plants.roles;
  }
}

/**
 * If there is an operation id, display users for that operation
 * otherwise display all users
 */
function getOperationUsers(opId) {
  if (opId) {
    operationUsers.value = operations.value.find((operation) => operation.id === opId).users;
  } else {
    switch (route.params.type) {
      case 'shafts':
        operationUsers.value = userData.value.shafts.users;
        roles.value = userData.value.shafts.roles;
        break;
      case 'shafts_v2':
        operationUsers.value = userData.value.shafts_v2_shafts.users;
        roles.value = userData.value.shafts_v2_shafts.roles;
        break;
      case 'ships':
        operationUsers.value = userData.value.ships.users;
        roles.value = userData.value.ships.roles;
        break;
      case 'equipment':
        operationUsers.value = userData.value.equipment.users;
        roles.value = userData.value.equipment.roles;
        break;
      default:
        operationUsers.value = userData.value.plants.users;
        roles.value = userData.value.plants.roles;
    }
  }
}

/**
 * Open operations panel on mobile
 */
function mobileOperationsPanelOpen() {
  mobilePanelShowing.value = true;
}

/**
 * Close operations panel on mobile
 */
function mobileOperationsPanelClose() {
  mobilePanelShowing.value = false;
}

/**
 * open search panel on mobile
 */
function openMobileSearch() {
  mobileSearchOpen.value = true;
}

/**
 * close and clear search panel on mobile
 */
function clearSearch() {
  mobileSearchOpen.value = false;
  search.value = '';
}

/**
 * Get the active panel index
 */
function fetchPanelIndex(name, index) {
  const operationName = name === 'shafts_v2_shafts' ? 'shafts_v2' : name;
  if (route.params.type === operationName) return true;
  return !isNaN(fetchIndex(storageName, index));
}

/**
 * Get the right route type for the all users button
 */
function getType(name) {
  return name === 'shafts_v2_shafts' ? 'shafts_v2' : name;
}

/**
 * Get the active back operation
 */
function mapNameToIndex(name) {
  switch (name) {
    case 'shafts':
      return 1;
    case 'shafts_v2':
      return 2;
    case 'ships':
      return 3;
    case 'equipment':
      return 4;
    default:
      return 0;
  }
}
</script>
